import { RouterModule, Routes } from '@angular/router';
import { authGuard, adminGuard, loginGuard, permissionGuard, maintenanceAccessGuard } from './guards/auth.guard';
import { PathwaysPermission } from './models/permissions.enum';
import { NgModule } from '@angular/core';

// PAT-192 — OI skills inventory under shared app shell (`#/skills` child routes).
// PAT-203 — admin console home under shared app shell (`#/admin`).
export const routes: Routes = [
  // PAT-192 — legacy `#/skills-inventory/*` deep links → `#/skills/*`.
  {
    path: 'skills-inventory/new',
    redirectTo: 'skills/new',
    pathMatch: 'full',
  },
  {
    path: 'skills-inventory/:slug',
    redirectTo: 'skills/:slug',
  },
  {
    path: 'skills-inventory',
    redirectTo: 'skills',
    pathMatch: 'full',
  },
  {
    path: 'skills-inventory/settings/admin',
    redirectTo: 'admin/skill-configuration',
    pathMatch: 'full',
  },
  {
    path: 'skills/settings/admin',
    redirectTo: 'admin/skill-configuration',
    pathMatch: 'full',
  },
  {
    path: 'setSID/:uid/:aid',
    loadComponent: () =>
      import('./pages/set-session-id/set-session-id.component').then(m => m.SetSessionIdComponent),
  },
  {
    path: 'setSID/:callingfrom/:tenantid/:uid/:aid',
    loadComponent: () =>
      import('./pages/set-session-id/set-session-id.component').then(m => m.SetSessionIdComponent),
  },
  {
    path: 'setSID/:callingfrom/:tenantid/:uid/:aid/:ausid/:claimprefix/:module',
    loadComponent: () =>
      import('./pages/set-session-id/set-session-id.component').then(m => m.SetSessionIdComponent),
  },
  {
    path: 'clrSID',
    loadComponent: () =>
      import('./pages/set-session-id/set-session-id.component').then(m => m.SetSessionIdComponent),
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [loginGuard],
  },
  {
    path: 'archive',
    loadComponent: () => import('./pages/archive/archive.component').then(m => m.ArchiveComponent),
    canActivate: [authGuard],
  },
  {
    path: 'recents',
    loadComponent: () => import('./pages/recents/recents.component').then(m => m.RecentsComponent),
    canActivate: [authGuard],
  },
  {
    path: 'share/:slug/:id',
    loadComponent: () =>
      import('./pages/shared-conversation/shared-conversation.component').then(
        m => m.SharedConversationComponent,
      ),
  },
  {
    path: 'shared/:token',
    loadComponent: () =>
      import('./pages/shared-conversation/shared-conversation.component').then(
        m => m.SharedConversationComponent,
      ),
  },
  // PAT-192 — authenticated app shell: `/chat` + `/skills/*` share ChatLayoutComponent chrome.
  {
    path: '',
    loadComponent: () => import('./pages/chat-layout/chat-layout.component').then(m => m.ChatLayoutComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'chat', pathMatch: 'full' },
      {
        path: 'chat',
        loadComponent: () =>
          import('./pages/chat-layout/chat-main.component').then(m => m.ChatMainComponent),
      },
      {
        path: 'skills/new',
        canActivate: [permissionGuard(PathwaysPermission.MasterSkillsInventory)],
        loadComponent: () =>
          import('./pages/skills-inventory/skills-inventory.component').then(
            m => m.SkillsInventoryComponent,
          ),
        data: { openIntake: true },
      },
      {
        path: 'skills/:slug',
        canActivate: [permissionGuard(PathwaysPermission.MasterSkillsInventory)],
        loadComponent: () =>
          import('./pages/skills-inventory/skill-detail.component').then(m => m.SkillDetailComponent),
      },
      {
        path: 'skills',
        canActivate: [permissionGuard(PathwaysPermission.MasterSkillsInventory)],
        loadComponent: () =>
          import('./pages/skills-inventory/skills-inventory.component').then(
            m => m.SkillsInventoryComponent,
          ),
      },
      {
        path: 'teams-call',
        loadComponent: () =>
          import('./pages/teams-call/teams-call.component').then(m => m.TeamsCallComponent),
      },
      {
        path: 'document-merge',
        loadComponent: () =>
          import('./pages/document-merge/document-merge.component').then(m => m.DocumentMergeComponent),
        // PAT-479 — prompt when leaving mid-merge via the portal logo or other nav.
        canDeactivate: [
          (component: { confirmLeaveToHome(): boolean | Promise<boolean> }) =>
            component.confirmLeaveToHome(),
        ],
      },
      // PAT-575 — BIOTECH AR claims pipeline (`#/biotech-pipeline`), gated on 25.5.P3.
      {
        path: 'biotech-pipeline',
        canActivate: [permissionGuard(PathwaysPermission.MasterBiotechArReport)],
        loadComponent: () =>
          import('./pages/biotech-pipeline/biotech-pipeline.component').then(
            m => m.BiotechPipelineComponent,
          ),
      },
      // PAT-203 — admin console nested routes (`#/admin/overview`, members, analytics/usage).
      {
        path: 'admin',
        loadComponent: () =>
          import('./pages/admin/admin-layout.component').then(m => m.AdminLayoutComponent),
        canActivate: [adminGuard, permissionGuard(PathwaysPermission.MasterAdminConsole)],
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./pages/admin/admin-overview.component').then(m => m.AdminOverviewComponent),
          },
          {
            path: 'members',
            loadComponent: () =>
              import('./pages/admin/admin-members.component').then(m => m.AdminMembersComponent),
          },
          {
            // PAT-354 — app feedback inbox (portal admin)
            path: 'feedback',
            loadComponent: () =>
              import('./pages/admin/admin-feedback.component').then(m => m.AdminFeedbackComponent),
          },
          {
            path: 'settings',
            canActivate: [permissionGuard(PathwaysPermission.AdminSettings)],
            loadComponent: () =>
              import('./pages/admin/admin-settings.component').then(m => m.AdminSettingsComponent),
          },
          {
            path: 'analytics/usage',
            canActivate: [permissionGuard(PathwaysPermission.AdminUsage)],
            loadComponent: () =>
              import('./pages/admin/admin-usage.component').then(m => m.AdminUsageComponent),
          },
          {
            path: 'analytics/document-merge',
            canActivate: [permissionGuard(PathwaysPermission.AdminUsage)],
            loadComponent: () =>
              import('./pages/admin/admin-document-merge.component').then(
                m => m.AdminDocumentMergeComponent,
              ),
          },
          {
            path: 'system',
            canActivate: [maintenanceAccessGuard],
            loadComponent: () =>
              import('./pages/admin/admin-system.component').then(m => m.AdminSystemComponent),
          },
          {
            path: 'models',
            canActivate: [permissionGuard(PathwaysPermission.AdminModels)],
            loadComponent: () =>
              import('./pages/admin/admin-models.component').then(m => m.AdminModelsComponent),
          },
          {
            path: 'mcp-connectors',
            canActivate: [permissionGuard(PathwaysPermission.AdminMcpConnectors)],
            loadComponent: () =>
              import('./pages/admin/admin-mcp-connectors.component').then(
                m => m.AdminMcpConnectorsComponent,
              ),
          },
          {
            path: 'skill-configuration',
            canActivate: [permissionGuard(PathwaysPermission.AdminSkillConfiguration)],
            loadComponent: () =>
              import('./pages/admin/admin-skill-configuration.component').then(
                m => m.AdminSkillConfigurationComponent,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutesModule {}
